/**
 * <Spinner> — CSS-only indeterminate rotation indicator.
 *
 * Use for short background ops (< 200ms per UI-SPEC — for longer loads,
 * use `<Skeleton>` instead). SVG circle with a 25% arc, rotated via the
 * Tailwind-core `animate-spin` utility. Respects `prefers-reduced-motion`
 * (falls back to a static ring — still visible, no perceived motion).
 */
import * as React from "react";
import { cn, cva, type VariantProps } from "../../utils";

export const spinnerVariants = cva("inline-block align-middle", {
  variants: {
    size: {
      sm: "w-4 h-4",
      md: "w-5 h-5",
      lg: "w-6 h-6",
    },
  },
  defaultVariants: { size: "md" },
});

export interface SpinnerProps extends VariantProps<typeof spinnerVariants> {
  /**
   * Accessible label. Optional — when omitted, the spinner is decorative and
   * gets `aria-hidden`. When provided, it becomes a live region announcing
   * the loading state.
   */
  "aria-label"?: string;
  className?: string;
}

export const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(function Spinner(
  { size, className, "aria-label": ariaLabel },
  ref,
) {
  const isDecorative = !ariaLabel || ariaLabel.length === 0;

  return (
    <svg
      ref={ref}
      className={cn(
        spinnerVariants({ size }),
        // Tailwind core spin keyframes; reduced-motion disables rotation.
        "animate-spin motion-reduce:animate-none",
        "text-primary",
        className,
      )}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...(isDecorative
        ? { "aria-hidden": true }
        : { role: "status", "aria-label": ariaLabel, "aria-live": "polite" })}
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12A10 10 0 0 0 12 2"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
});
